// ClearableTextInput.jsx
import React from "react";

import { TestableTextInput } from "./TestableComponents";

import PropTypes from "prop-types";

/**
 * Clearable Text Input Component - adds a button to allow one click clearing of the input field.
 * 
 * @param {string} value - The current value of the input field.
 * @param {function} onChange - Function to call when the input value changes.
 * @param {function} [onClear] - Optional function to call when the clear button is clicked. If not provided, the input will be cleared by setting value to an empty string.
 * @param {string} dataTestId - The data-testid attribute for testing purposes.
 * @param {string} [className] - Additional CSS classes to apply to the input field.
 * @param {Object} props - Additional props to pass to the input component.
 * @returns {JSX.Element}
 */
export default function ClearableTextInput({ value, onChange, onClear, dataTestId, className = "", ...props}) {

    const handleClear = () => {
        if (onClear) {
            onClear();
            return;
        } 
        
        onChange({ target: { value: "" } });
    };

    return (
        <div className="relative">
            <TestableTextInput
                value={value}
                onChange={onChange}
                dataTestId={dataTestId}
                className={`pr-8 ${className}`}
                {...props}
            />
            {value && (
                <button
                    onClick={handleClear}
                    data-testid={`${dataTestId}-clear-button`}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-xs transition-colors"
                    type="button"
                >
                ×
                </button>
            )}
        </div>
    );
};

ClearableTextInput.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onClear: PropTypes.func,
    dataTestId: PropTypes.string.isRequired,
    className: PropTypes.string,
};